import * as React from 'react'

import OptionsItem from '~/client/src/shared/components/OptionsList/pieces/OptionsItem'
import OptionsItemList from '~/client/src/shared/components/OptionsList/pieces/OptionsItemList'
import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { parseHierarchyOptions } from '~/client/src/shared/utils/hierarchyTree'
import { getDisplayValue } from '~/client/src/shared/utils/propertyDisplayUtils'
import { toast } from '~/client/src/shared/utils/toast'

import { IResourcePropertyConfig } from '../types'
import { useOptimisticCellValue } from './useOptimisticCellValue'

interface IProps {
  value: string | null
  propertyConfig?: IResourcePropertyConfig
  /** The column name, the accessible name of the cell's dropdown trigger. */
  label: string
  /** Without it the cell only displays its value. */
  onChange?: (value: string | null) => Promise<void>
}

interface ISelectCellOption {
  value: string | null
  label: string
}

// Live options, plus the record's own value when its option was deleted, so it still reads
// as selected - the same list the resource card's Select field offers.
export function getCellOptions(
  value: string | null,
  config?: IResourcePropertyConfig,
): ISelectCellOption[] {
  const options: ISelectCellOption[] = parseHierarchyOptions(config?.options)
    .filter(option => !option.deleted || option.value === value)
    .map(option => ({ value: option.value, label: option.label }))

  return config?.includeNoneOption
    ? [{ value: null, label: Localization.translator.none }, ...options]
    : options
}

export default function SelectCell({
  value,
  propertyConfig,
  label,
  onChange,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const { shownValue, isSaving, commit } = useOptimisticCellValue(
    value,
    nextValue => onChange?.(nextValue) ?? Promise.resolve(),
    error => {
      console.error('Failed to save the cell value:', error)
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
    },
  )

  const text = shownValue ? getDisplayValue(shownValue, propertyConfig) : null
  const display = text ? (
    <span className="tw:truncate" title={text}>
      {text}
    </span>
  ) : null

  const select = (nextValue: string | null) => {
    setIsOpen(false)
    void commit(nextValue)
  }

  if (!onChange) return display

  return (
    // The row opens the resource card on click, and the portaled popover's clicks bubble
    // to it through React, so the cell keeps them.
    <div
      className="tw:h-full tw:w-full tw:min-w-0"
      onClick={event => event.stopPropagation()}
    >
      <PopoverTagPicker
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        placeholder=""
        triggerLabel={label}
        variant="cell"
        cellAffordance="chevron"
        displayOnly={isSaving}
        popoverAlign="start"
        popoverClassName="tw:w-64"
        popoverContent={
          <OptionsItemList className="tw:max-h-80 tw:p-1">
            {getCellOptions(shownValue, propertyConfig).map(option => (
              <OptionsItem
                key={option.value ?? ''}
                label={option.label}
                selected={option.value === (shownValue ?? null)}
                onClick={() => select(option.value)}
              />
            ))}
          </OptionsItemList>
        }
      >
        {display}
      </PopoverTagPicker>
    </div>
  )
}
